import { useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { ChevronDown, ChevronsLeft, ChevronsRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { APP_NAV, type NavItem, type NavSection } from "@/app/constants";

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

export function Sidebar({ collapsed, onToggle }: SidebarProps) {
  return (
    <aside
      className={cn(
        "fixed inset-y-0 left-0 z-30 flex flex-col bg-navy text-white transition-all duration-300",
        collapsed ? "w-16" : "w-[200px]",
      )}
    >
      {/* Brand */}
      <div className="flex h-16 items-center gap-3 border-b border-white/10 px-4">
        <span className="shrink-0 text-2xl text-rose">◉</span>
        {!collapsed && (
          <span className="truncate text-lg font-semibold tracking-[0.18em] text-rose">
            BIZPANDIT
          </span>
        )}
      </div>

      {/* Nav items */}
      <nav className="flex-1 space-y-1 overflow-y-auto px-2 py-4">
        {APP_NAV.map((entry, i) =>
          entry.kind === "item" ? (
            <NavLeafRow key={entry.href} item={entry} collapsed={collapsed} />
          ) : (
            <NavSectionRow
              key={`sec-${i}`}
              section={entry}
              collapsed={collapsed}
            />
          ),
        )}
      </nav>

      {/* Collapse toggle */}
      <button
        type="button"
        onClick={onToggle}
        className="flex items-center gap-2 border-t border-white/10 px-4 py-3 text-sm font-medium text-white/60 transition-colors hover:bg-white/10 hover:text-white"
      >
        {collapsed ? (
          <ChevronsRight className="h-4 w-4" />
        ) : (
          <ChevronsLeft className="h-4 w-4" />
        )}
        {!collapsed && <span>Collapse</span>}
      </button>
    </aside>
  );
}

/* ---------------------------------------------------------------- */
/* Row for a flat nav item                                          */
/* ---------------------------------------------------------------- */
function NavLeafRow({
  item,
  collapsed,
  nested,
}: {
  item: NavItem;
  collapsed: boolean;
  nested?: boolean;
}) {
  return (
    <NavLink
      to={item.href}
      title={collapsed ? item.label : undefined}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors",
          nested && !collapsed && "pl-9",
          isActive
            ? "bg-rose text-roseink"
            : "text-white/70 hover:bg-white/10 hover:text-white",
        )
      }
    >
      <item.icon className="h-5 w-5 shrink-0" />
      {!collapsed && <span className="truncate">{item.label}</span>}
    </NavLink>
  );
}

/* ---------------------------------------------------------------- */
/* Collapsible section (e.g. "Admin")                               */
/* ---------------------------------------------------------------- */
function NavSectionRow({
  section,
  collapsed,
}: {
  section: NavSection;
  collapsed: boolean;
}) {
  const location = useLocation();
  const containsActive = useMemo(
    () => section.items.some((it) => location.pathname.startsWith(it.href)),
    [location.pathname, section.items],
  );
  const [open, setOpen] = useState<boolean>(containsActive);

  // When sidebar is collapsed, render children as tooltipped icons directly.
  if (collapsed) {
    return (
      <div className="space-y-1">
        <div
          className="flex items-center justify-center rounded-xl px-3 py-2 text-white/40"
          title={section.label}
        >
          <section.icon className="h-5 w-5" />
        </div>
        {section.items.map((it) => (
          <NavLeafRow key={it.href} item={it} collapsed />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold uppercase tracking-wide transition-colors",
          containsActive
            ? "text-white"
            : "text-white/60 hover:bg-white/10 hover:text-white",
        )}
      >
        <section.icon className="h-5 w-5 shrink-0" />
        <span className="flex-1 truncate text-left">{section.label}</span>
        <ChevronDown
          className={cn(
            "h-4 w-4 shrink-0 transition-transform",
            open ? "rotate-0" : "-rotate-90",
          )}
        />
      </button>
      {open &&
        section.items.map((it) => (
          <NavLeafRow key={it.href} item={it} collapsed={collapsed} nested />
        ))}
    </div>
  );
}
